import { interpret, type Actor, type State } from '@heidi/flow/state-machine';
import {
  artifactGenerationMachine,
  type ArtifactGenerationContext,
  type ArtifactGenerationEvent,
  type ArtifactGenerationPhase,
} from './artifact-generation.machine';

export type ArtifactGenerationKey =
  | { kind: 'note'; sessionId: string }
  | { kind: 'document'; sessionId: string; documentId: string };

export type ArtifactGenerationSnapshot = State<
  ArtifactGenerationContext,
  ArtifactGenerationPhase
>;

type ArtifactGenerationActor = Actor<
  ArtifactGenerationContext,
  ArtifactGenerationPhase,
  ArtifactGenerationEvent
>;

type RegistryEntry = {
  actor: ArtifactGenerationActor;
  subscribers: number;
};

let nextGenerationId = 0;
const registry = new Map<string, RegistryEntry>();

export const artifactGenerationKey = (key: ArtifactGenerationKey): string =>
  key.kind === 'note'
    ? `note:${key.sessionId}`
    : `document:${key.sessionId}:${key.documentId}`;

const createEntry = (): RegistryEntry => ({
  actor: interpret<
    ArtifactGenerationContext,
    ArtifactGenerationPhase,
    ArtifactGenerationEvent
  >(artifactGenerationMachine).start(),
  subscribers: 0,
});

const getEntry = (key: ArtifactGenerationKey): RegistryEntry => {
  const id = artifactGenerationKey(key);
  const existing = registry.get(id);
  if (existing) return existing;
  const entry = createEntry();
  registry.set(id, entry);
  return entry;
};

const maybeEvict = (key: ArtifactGenerationKey, entry: RegistryEntry) => {
  const phase = entry.actor.getSnapshot().value;
  if (
    entry.subscribers > 0 ||
    (phase !== 'idle' && phase !== 'aborted' && phase !== 'failed')
  ) {
    return;
  }
  entry.actor.stop();
  registry.delete(artifactGenerationKey(key));
};

export const getArtifactGenerationSnapshot = (
  key: ArtifactGenerationKey
): ArtifactGenerationSnapshot => getEntry(key).actor.getSnapshot();

export const getInitialArtifactGenerationSnapshot = (
  key: ArtifactGenerationKey
): ArtifactGenerationSnapshot => getEntry(key).actor.getInitialSnapshot();

export const subscribeArtifactGeneration = (
  key: ArtifactGenerationKey,
  listener: () => void
): (() => void) => {
  const entry = getEntry(key);
  entry.subscribers += 1;
  const unsubscribe = entry.actor.subscribe(listener);
  return () => {
    unsubscribe();
    entry.subscribers -= 1;
    maybeEvict(key, entry);
  };
};

export const beginArtifactGeneration = ({
  key,
  outgoingContent,
  controller = new AbortController(),
  phase = 'awaiting',
}: {
  key: ArtifactGenerationKey;
  outgoingContent: string | null;
  controller?: AbortController;
  phase?: 'uploading' | 'awaiting';
}): { generationId: number; controller: AbortController } => {
  const entry = getEntry(key);
  const snapshot = entry.actor.getSnapshot();
  const previousController = snapshot.context.controller;
  const isLiveStream =
    snapshot.value === 'awaiting' ||
    snapshot.value === 'streaming' ||
    snapshot.value === 'finalizing';

  // Stop-recording onMutate is lifecycle bookkeeping, not a new generation.
  // It must not abort or restart an in-flight note/document stream.
  if (phase === 'uploading' && isLiveStream) {
    return {
      generationId: snapshot.context.generationId,
      controller: previousController ?? controller,
    };
  }

  if (
    previousController &&
    previousController !== controller &&
    !previousController.signal.aborted
  ) {
    previousController.abort('superseded');
  }

  // A synchronous abort listener may call abortArtifactGeneration and evict
  // the captured entry. Re-resolve before START so we never send to a stopped actor.
  const liveEntry = getEntry(key);
  const generationId = ++nextGenerationId;
  liveEntry.actor.send({
    type: phase === 'uploading' ? 'START_UPLOADING' : 'START_AWAITING',
    generationId,
    outgoingContent,
    controller,
  });
  return { generationId, controller };
};

export const updateArtifactGenerationProgress = (
  key: ArtifactGenerationKey,
  progress?: number
) => getEntry(key).actor.send({ type: 'PROGRESS', progress });

export const markArtifactGenerationAwaiting = (key: ArtifactGenerationKey) =>
  getEntry(key).actor.send('AWAIT');

export const markArtifactGenerationStreamOpen = (key: ArtifactGenerationKey) =>
  getEntry(key).actor.send('STREAM_OPEN');

export const markArtifactGenerationChunkReceived = (key: ArtifactGenerationKey) => {
  const entry = getEntry(key);
  if (entry.actor.getSnapshot().context.firstChunkReceived) return;
  entry.actor.send('CHUNK');
};

export const markArtifactGenerationFinalizing = (key: ArtifactGenerationKey) =>
  getEntry(key).actor.send('FINALIZE');

export const completeArtifactGeneration = (key: ArtifactGenerationKey) => {
  const entry = getEntry(key);
  const phase = entry.actor.getSnapshot().value;
  entry.actor.send(phase === 'aborted' || phase === 'failed' ? 'RESET' : 'COMPLETE');
  maybeEvict(key, entry);
};

export const failArtifactGeneration = (key: ArtifactGenerationKey, error: unknown) => {
  const entry = getEntry(key);
  entry.actor.send({ type: 'FAIL', error });
  maybeEvict(key, entry);
};

export const abortArtifactGeneration = (
  key: ArtifactGenerationKey,
  reason: unknown = 'user'
): boolean => {
  const entry = registry.get(artifactGenerationKey(key));
  if (!entry) return false;
  const { controller } = entry.actor.getSnapshot().context;
  if (controller && !controller.signal.aborted) controller.abort(reason);
  entry.actor.send('ABORT');
  maybeEvict(key, entry);
  return true;
};

export const resetArtifactGeneration = (key: ArtifactGenerationKey) => {
  const entry = registry.get(artifactGenerationKey(key));
  if (!entry) return;
  entry.actor.send('RESET');
  maybeEvict(key, entry);
};

export const isArtifactGenerationActive = (
  snapshot: ArtifactGenerationSnapshot
): boolean =>
  snapshot.value === 'uploading' ||
  snapshot.value === 'awaiting' ||
  snapshot.value === 'streaming' ||
  snapshot.value === 'finalizing';

export const stopAllArtifactGenerations = (reason: unknown = 'cleanup') => {
  registry.forEach(({ actor }) => {
    const controller = actor.getSnapshot().context.controller;
    if (controller && !controller.signal.aborted) controller.abort(reason);
    actor.send('ABORT');
    actor.stop();
  });
  registry.clear();
};

export const resetArtifactGenerationCoordinatorForTests = () => {
  stopAllArtifactGenerations('cleanup');
  nextGenerationId = 0;
};
